import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiClient } from '../../lib/api';
import { PageStage } from '../../components/PageStage';
import { SectionHead } from '../../components/SectionHead';
import { Input } from '../../components/Input';
import type { Partner } from '../../lib/types';
import { toast } from '../../lib/toast';
import styles from './Partners.module.css';

export function PartnersPage() {
  const [items, setItems] = useState<Partner[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Backend returns paginated {items, total, ...}, not a bare array.
        const res = await apiClient.get<{ items: Partner[] } | Partner[]>(
          '/api/v1/crm/partners',
        );
        const list = Array.isArray(res) ? res : (res?.items ?? []);
        setItems(list);
      } catch (err) {
        if (err instanceof ApiError && err.status !== 401) {
          toast.error('Не удалось загрузить картотеку.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = q
    ? items.filter((p) =>
        ((p.full_name_masked || p.full_name || '') + (p.function_tags ?? []).join(' '))
          .toLowerCase()
          .includes(q.toLowerCase()),
      )
    : items;

  return (
    <PageStage>
      <SectionHead
        eyebrow="Картотека"
        title="Партнёры"
        italic="сети"
        sub="Личный архив. Не CRM компании. Записи зашифрованы."
      />

      <div className={styles.toolbar}>
        <Input
          label="Поиск"
          placeholder="по имени или роли — italian_speakers, art_world…"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        <div className={styles.count}>{filtered.length} в картотеке</div>
      </div>

      {loading ? (
        <p className={styles.muted}>загружаем…</p>
      ) : filtered.length === 0 ? (
        <p className={styles.muted}>
          {items.length === 0
            ? 'Картотека пока пуста. Заведите первого партнёра через беседу или онбординг.'
            : 'Ничего не нашлось.'}
        </p>
      ) : (
        <ul className={styles.grid}>
          {filtered.map((p) => (
            <li key={p.id}>
              <Link to={`/partners/${p.id}`} className={styles.card}>
                <div className={styles.cardHead}>
                  <span className={styles.cardName}>
                    {p.full_name_masked || p.full_name || '—'}
                  </span>
                  <span className={styles.cardLevel}>
                    близость · {p.intimacy_level}/5
                  </span>
                </div>
                <div className={styles.cardTags}>
                  {(p.function_tags ?? []).slice(0, 4).map((t) => (
                    <span key={t} className={styles.tag}>
                      {t}
                    </span>
                  ))}
                </div>
                <div className={styles.cardMeta}>
                  важность {p.importance}/5 · температура {Math.round(p.temperature)}/100
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageStage>
  );
}
